import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  Assessment_Activity_Status_Enum,
  Assessment_Activity_Type_Enum,
  InsertAssessmentActivityWithLinkedItemsMutation,
  InsertAssessmentActivityWithLinkedItemsMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertAssessmentActivityWithLinkedItemsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

/**
 * tRPC input type for insertActivity.
 * Uses explicit type instead of GraphQL MutationVariables to avoid
 * the codegen `Array<T> | T` issue with inline variables.
 */
type InsertAssessmentActivityTRPCInput = {
  ActivityType: Assessment_Activity_Type_Enum;
  ParentId: string;
  Status: Assessment_Activity_Status_Enum;
  Summary?: string | null;
  Title: string;
  CompletionDate?: string | null;
  AssignedUser?: string | null;
  LinkedItemIds: string[];
  CustomAttributeData?: CustomAttributeData | null;
  IsRCSA?: boolean;
  RiskId?: string | null;
  OwnerUserIds?: string[];
  OwnerGroupIds?: string[];
};

export const useInsertAssessmentActivityWithLinkedItems = createMutationHook({
  name: 'insertAssessmentActivity',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.assessment.insertActivity.mutationOptions({ onSuccess }),
  // The codegen list-scalar variables widen to `T | T[]` — normalise them to
  // plain arrays for the tRPC procedure.
  mapInput: (
    variables: InsertAssessmentActivityWithLinkedItemsMutationVariables
  ): InsertAssessmentActivityTRPCInput => ({
    ActivityType: variables.ActivityType,
    ParentId: variables.ParentId,
    Status: variables.Status,
    Summary: variables.Summary,
    Title: variables.Title,
    CompletionDate: variables.CompletionDate,
    AssignedUser: variables.AssignedUser,
    LinkedItemIds:
      variables.LinkedItemIds == null
        ? []
        : Array.isArray(variables.LinkedItemIds)
          ? variables.LinkedItemIds
          : [variables.LinkedItemIds],
    CustomAttributeData: variables.CustomAttributeData,
    IsRCSA: variables.IsRCSA ?? undefined,
    RiskId: variables.RiskId,
    OwnerUserIds:
      variables.OwnerUserIds == null
        ? []
        : Array.isArray(variables.OwnerUserIds)
          ? variables.OwnerUserIds
          : [variables.OwnerUserIds],
    OwnerGroupIds:
      variables.OwnerGroupIds == null
        ? []
        : Array.isArray(variables.OwnerGroupIds)
          ? variables.OwnerGroupIds
          : [variables.OwnerGroupIds],
  }),
  invalidate: (trpc) => [
    // COI-889: router-level key — the hand-listed set missed `activityCount`
    // and `oversightActivityCount`, both left-nav badges.
    trpc.frontend.assessment.pathKey(),
    // COI-471 parity: Apollo twin also evicts `internal_audit_entity`,
    // `internal_audit_report` and `oversight_assessment`.
    trpc.frontend.internalAuditEntity.pathKey(),
    trpc.frontend.internalAuditReport.pathKey(),
    trpc.frontend.oversight.pathKey(),
    // COI-889: the my-items ribbon counts this entity.
    trpc.frontend.myItems.pathKey(),
  ],
  mapResponse: (result: {
    Id: string;
  }): InsertAssessmentActivityWithLinkedItemsMutation => ({
    insertAssessmentActivityWithLinkedItems: {
      __typename: 'IdOutput',
      Id: result.Id,
    },
  }),
  graphqlDocument: InsertAssessmentActivityWithLinkedItemsDocument,
  graphqlVariables: (
    variables: InsertAssessmentActivityWithLinkedItemsMutationVariables
  ) => ({ ...variables }),
  evictFields: [
    'assessment',
    'oversight_assessment',
    'internal_audit_entity',
    'internal_audit_report',
    'assessment_activity',
    'assessment_activity_aggregate',
  ],
});
